/**
 * A KÉRETLEN FELTÖLTÉS LAPJA — „Mi ez?", aztán „Hogyan küldöd?" (#85).
 *
 * A mérce: `docs/tervezes/design/kliens-uj-kinezet/a-kiolvasott-nev-es-tipus.html`,
 * 2. szakasz (Viktor döntése, 2026-11-01).
 *
 * ═══ MIÉRT ELŐBB A KÉRDÉS ═══
 *
 * Eddig a „Mi ez?" utólag, a nyugta sorában jött, és könnyű volt átlépni —
 * a kéretlen feltöltések nagy része típus nélkül érkezett. Most akkor
 * kérdezünk, amikor a papír még az ügyfél kezében van, és tudja, mi az.
 *
 * ═══ A LAP RÉTEG, NEM OLDAL ═══
 *
 * A feltöltés MŰVELET (`CLAUDE.md` §2): a lap a munkaképernyő fölött nyílik,
 * és bezárva minden ott marad, ahol volt. Útvonal nincs.
 *
 * ═══ A „NEM TUDOM" EGYENRANGÚ VÁLASZ ═══
 *
 * Ugyanaz a menekülőút, mint a lista választójában: szaggatott keret, saját
 * magyarázat, és a `clientDocType` `NULL` marad. A „Mégsem" ettől különbözik:
 * az nem válasz, hanem kilépés — feltöltés sem lesz.
 *
 * ═══ HA NINCS MIT KÉRDEZNI ═══
 *
 * Ha a cégnél egyetlen típus sem választható (#25/3), a kérdés kimarad, és a
 * lap a második lépéssel nyílik. Egy csak „Nem tudom"-ot kínáló kérdés
 * fölösleges koppintás volna.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CameraIcon, FileIcon } from '@/components/common';
import { CameraSheet } from '@/features/capture/CameraSheet';
import { trapTab } from '@/lib/focus-trap';
import { docTypeHint, docTypeLabel, offeredDocTypes } from './docTypeChoices';
import { FilePicker } from './FilePicker';
import { GroupSheet } from './GroupSheet';
import { needsGrouping } from './grouping';
import type { ClientDocType } from './types';

interface UploadSheetProps {
  /** A cég engedélyezett típusai; `null` = a szerver nem mondta meg, mind felkínálva. */
  allowed: readonly ClientDocType[] | null;
  /** A kamera félbehagyott sorozatának kulcsa (`freeCaptureKey`); `null` = nincs megőrzés. */
  storageKey: string | null;
  onClose: () => void;
  /** A választott fájlok és a „Mi ez?" válasza — `null` = „Nem tudom". */
  onFiles: (files: File[], docType: ClientDocType | null) => void;
  /** A kamera sorozata — EGY bizonylat oldalai. */
  onPages: (pages: Blob[], docType: ClientDocType | null) => void;
  /** Több galériakép, csoportokban (#50) — csoportonként EGY bizonylat. */
  onGroups: (groups: File[][], docType: ClientDocType | null) => void;
}

type Step = { kind: 'type' } | { kind: 'how'; docType: ClientDocType | null };

export function UploadSheet({
  allowed,
  storageKey,
  onClose,
  onFiles,
  onPages,
  onGroups,
}: UploadSheetProps) {
  const { t } = useTranslation();
  const offered = offeredDocTypes(allowed);
  const [step, setStep] = useState<Step>(
    offered.length === 0 ? { kind: 'how', docType: null } : { kind: 'type' }
  );
  const [cameraOpen, setCameraOpen] = useState(false);
  /** A galériából választott képek, amíg az összefűző áttekintő nyitva van (#50). */
  const [grouping, setGrouping] = useState<File[] | null>(null);
  const titleId = useId();
  const title = useRef<HTMLHeadingElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // A FÓKUSZ A LAP CÍMÉRE UGRIK, lépésenként újra: a képernyőolvasó így
  // kimondja, hol tart, és a billentyűzet nem marad a lap mögötti gombon.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a lépésváltás maga a kiváltó ok
  useEffect(() => {
    title.current?.focus();
  }, [step.kind]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // A kamera és az összefűző áttekintő saját réteg — a billentyűik
      // az övék, nem a lap mögöttük.
      if (cameraOpen || grouping !== null) {
        return;
      }
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      // A TAB A LAPON BELÜL MARAD (`aria-modal`). A lap mögötti gombok a
      // fátyol alatt vannak; ha a fókusz odaszökne, a billentyűzetes ügyfél
      // egy láthatatlan gombot nyomna meg.
      if (event.key === 'Tab' && panel.current) {
        trapTab(event, panel.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cameraOpen, grouping, onClose]);

  if (cameraOpen && step.kind === 'how') {
    const docType = step.docType;
    return (
      <CameraSheet
        storageKey={storageKey}
        // A FEJLÉC A VÁLASZT MUTATJA — ahogy a kérés-kártyáról indítva a kért
        // számla nevét. „Nem tudom"-nál a művelet neve áll ott.
        title={docType === null ? t('upload.anything') : docTypeLabel(t, docType)}
        onCancel={() => setCameraOpen(false)}
        onDone={(pages) => {
          setCameraOpen(false);
          onPages(pages, docType);
          onClose();
        }}
      />
    );
  }

  if (grouping !== null && step.kind === 'how') {
    const docType = step.docType;
    return (
      <GroupSheet
        files={grouping}
        together={false}
        onCancel={() => setGrouping(null)}
        onSend={(groups) => {
          setGrouping(null);
          onGroups(groups, docType);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="sheet-scrim">
      {/* A HÁTTÉRRE KOPPINTÁS BEZÁR. Az egér és az ujj útja; a billentyűzeté
          és a képernyőolvasóé a lap saját „Mégsem"-je és az Escape — ezért
          rejtett, különben két „Mégsem" állna a lapon. */}
      <button
        type="button"
        className="sheet-dismiss"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
      />
      <div ref={panel} className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <span className="sheet-grab" aria-hidden="true" />

        {step.kind === 'type' ? (
          <>
            <h2 id={titleId} ref={title} tabIndex={-1}>
              {t('docType.ask')}
            </h2>
            <p className="meta sheet-lead">{t('uploadSheet.askLead')}</p>

            {offered.map((docType) => (
              <button
                key={docType}
                type="button"
                className="sheet-option"
                onClick={() => setStep({ kind: 'how', docType })}
              >
                <span className="sheet-option-label">{docTypeLabel(t, docType)}</span>
                <span className="meta">{docTypeHint(t, docType)}</span>
              </button>
            ))}

            <button
              type="button"
              className="sheet-option sheet-option-unknown"
              onClick={() => setStep({ kind: 'how', docType: null })}
            >
              <span className="sheet-option-label">{t('docType.unknown')}</span>
              <span className="meta">{t('docType.unknownHint')}</span>
            </button>
          </>
        ) : (
          <>
            {offered.length === 0 ? null : (
              <p className="sheet-picked">
                <span>
                  {t('uploadSheet.picked')}{' '}
                  <strong>
                    {step.docType === null
                      ? t('uploadSheet.pickedUnknown')
                      : docTypeLabel(t, step.docType)}
                  </strong>
                </span>
                <button
                  type="button"
                  className="pick pick-link"
                  onClick={() => setStep({ kind: 'type' })}
                >
                  {t('uploadSheet.change')}
                </button>
              </p>
            )}

            <h2 id={titleId} ref={title} tabIndex={-1}>
              {t('uploadSheet.how')}
            </h2>

            <div className="request-actions">
              <button
                type="button"
                className="pick pick-tile pick-tile-primary"
                onClick={() => setCameraOpen(true)}
              >
                <span className="pick-icon">
                  <CameraIcon />
                </span>
                <span>{t('upload.choosePhoto')}</span>
              </button>
              <FilePicker
                label={t('upload.chooseFile')}
                variant="tile"
                icon={<FileIcon />}
                multiple
                onPick={(files) => {
                  // TÖBB KÉP: előbb az összefűzés (#50). Ha nem nyúl semmihez,
                  // ugyanaz megy el, ami eddig — képenként egy bizonylat.
                  if (needsGrouping(files)) {
                    setGrouping(files);
                    return;
                  }
                  onFiles(files, step.docType);
                  onClose();
                }}
              />
            </div>

            {/* A TÖBB FÁJLRA IS EGY VÁLASZ SZÓL — és ezt itt, a választás előtt
                kell kimondani, nem utólag a nyugtában. */}
            <p className="meta centered-text">
              {step.docType === null
                ? t('uploadSheet.manyNoteUnknown')
                : t('uploadSheet.manyNote', {
                    type: docTypeLabel(t, step.docType).toLocaleLowerCase('hu'),
                  })}
            </p>
          </>
        )}

        <button type="button" className="pick pick-link sheet-cancel" onClick={onClose}>
          {t('uploadSheet.cancel')}
        </button>
      </div>
    </div>
  );
}
